import { graphql } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { t } from 'i18next';
import { useQuery } from 'urql';
import { type RouteKey, routeTitles } from '../../constants/routeTitles';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const BreadcrumbPageQueryDocument = graphql(`
  query BreadcrumbPageData($profile: ID!, $organizationUuids: [ID!]!) {
    groups(where: { organization: { uuid_IN: $organizationUuids } }) {
      uuid
      __typename
      name
      teachingUnits {
        uuid
        __typename
        workMaterials {
          uuid
          __typename
          title
        }
      }
    }
    courses: groups(
      where: {
        type: course
        organization: { uuid_IN: $organizationUuids }
        AND: [
          { OR: [{ editors_SOME: { uuid: $profile } }, { viewers_SOME: { uuid: $profile } }] }
          { OR: [{ inCurrentSchoolyear: true }, { isCommunity: true }] }
        ]
      }
    ) {
      uuid
      __typename
      name
      teachingUnits {
        uuid
        __typename
        title
        workMaterials {
          uuid
          __typename
          title
        }
        assignments {
          uuid
          __typename
          title
          submissions {
            uuid
            __typename
            owner {
              uuid
              __typename
              displayName
            }
          }
        }
      }
    }
    classes(where: { organization: { uuid_IN: $organizationUuids } }) {
      __typename
      uuid
      name
    }
    profiles(where: { organization: { uuid_IN: $organizationUuids } }) {
      __typename
      uuid
      selectName
    }
  }
`);

type CrumbItemType = {
  slug: string;
  title?: string;
};

export const useBreadCrumbs = (pathnames: string[]) => {
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuids = pimAuthClaims.getAllowedOrganizationUuids();

  const [{ data }] = useQuery({
    query: BreadcrumbPageQueryDocument,
    variables: {
      profile: profileUuid,
      organizationUuids,
    },
    // sollte nur aufgerufen werden, wenn Breadcrumb das erste Mal geladen wir, oder Daten nachgeladen werden, daher 'cache-first'
    requestPolicy: 'cache-first',
  });

  const courses = data?.courses ?? [];
  const groups = data?.groups ?? [];
  const classes = data?.classes ?? [];
  const profiles = data?.profiles ?? [];

  const groupWorkMaterials = groups.flatMap((g) => g.teachingUnits.flatMap((t) => t.workMaterials)) ?? [];
  const lessons = courses.flatMap((c) => c.teachingUnits);
  const courseWorkMaterials = lessons.flatMap((c) => c.workMaterials) ?? [];
  const assignments = lessons.flatMap((l) => l.assignments);
  const submissions = assignments.flatMap((a) => a.submissions);
  const workMaterials = [...courseWorkMaterials, ...groupWorkMaterials];

  const crumbs: CrumbItemType[] = pathnames
    .map((path, index) => {
      const slug = '/' + pathnames.slice(0, index + 1).join('/');

      // Skip UUID Segmente in der Title Erstellung
      if (UUID_PATTERN.test(path)) {
        // Für UUID Segmente, nutze den vorherigen Segmenttitel um den Titel aus den Query Daten zu suchen
        const previousSegment = pathnames[index - 1] as RouteKey;
        let title: string | undefined;
        const fallbackTitle = '';

        switch (previousSegment) {
          case 'groups':
            title = groups.find((g) => g.uuid === pathnames[index])?.name ?? fallbackTitle;
            break;
          case 'courses':
            title = courses.find((c) => c.uuid === pathnames[index])?.name ?? fallbackTitle;
            break;
          case 'lessons':
            title = lessons.find((l) => l.uuid === pathnames[index])?.title ?? fallbackTitle;
            break;
          case 'workmaterials':
            title = workMaterials.find((w) => w.uuid === pathnames[index])?.title ?? fallbackTitle;
            break;
          case 'assignments':
            title = assignments.find((l) => l.uuid === pathnames[index])?.title ?? fallbackTitle;
            break;
          case 'submissions':
            title = `${t('submissions.title', { count: 1 })} ${String.fromCharCode(8211)} ${
              submissions?.find((l) => l.uuid === pathnames[index])?.owner.displayName
            }`;
            break;
          case 'public':
            title = `${t('submissions.title', { count: 1 })} ${String.fromCharCode(8211)} ${
              submissions.find((l) => l.uuid === pathnames[index])?.uuid
            }`;
            break;
          case 'messages':
            title = undefined;
            break;
          case 'evaluation':
            title = classes.find((c) => c.uuid === pathnames[index])?.name ?? fallbackTitle;
            break;
        }

        // classbook evaluation (double uuid -> first is class second is profile)
        if (UUID_PATTERN.test(path) && pathnames[index - 2] && pathnames[index - 2] === 'evaluation') {
          title = profiles.find((p) => p.uuid === pathnames[index])?.selectName ?? fallbackTitle;
        }

        if (!title) return undefined;

        return {
          slug,
          title,
        };
      }

      const routeKey = path as RouteKey;
      const title = routeTitles[routeKey];

      if (!title) return undefined;

      return { slug, title };
    })
    .filter((c) => c !== undefined);

  return crumbs;
};
